/**
 * @file Shared frontend date formatting and date comparison helpers.
 */

/**
 * Returns a date string in YYYY-MM-DD format based on local time.
 * This avoids the common "one day in the past" bug caused by using toISOString().
 * @param {Date | string | number} [dateInput=new Date()] - The date to format.
 * @returns {string} The local date string (YYYY-MM-DD).
 */
export const getLocalDateString = (dateInput = new Date()) => {
    try {
        const date = new Date(dateInput);
        if (isNaN(date.getTime())) {return '';}
        // Using en-CA locale because it consistently returns YYYY-MM-DD
        return date.toLocaleDateString('en-CA');
    } catch {
        return '';
    }
};

/**
 * Calculates the next service due date based on the last service date and frequency.
 * @param {string | null} lastServiceDate - The date of the last service (YYYY-MM-DD).
 * @param {string | number | null} frequencyMonths - The service frequency in months.
 * @returns {string | null} The calculated next service date (YYYY-MM-DD), or null if inputs are invalid.
 */
export const calculateNextServiceDate = (lastServiceDate, frequencyMonths) => {
    if (!lastServiceDate || !frequencyMonths || isNaN(parseInt(frequencyMonths, 10)) || parseInt(frequencyMonths, 10) <= 0) {
        return null; // Added check for non-positive frequency
    }
    try {
        const date = new Date(lastServiceDate);
        // Add validation for the input date string
        if (isNaN(date.getTime())) {
            console.warn(`Invalid lastServiceDate passed to calculateNextServiceDate: ${lastServiceDate}`);
            return null;
        }
        // Set to midday to avoid timezone issues when adding months
        date.setHours(12, 0, 0, 0);
        date.setMonth(date.getMonth() + parseInt(frequencyMonths, 10));
        // Return in YYYY-MM-DD format suitable for date inputs (Local Time)
        return getLocalDateString(date);
    } catch (error) {
        console.error(`Error calculating next service date with inputs: ${lastServiceDate}, ${frequencyMonths}`, error);
        return null;
    }
};

/**
 * Calculates the start (Monday 00:00) and end (Sunday 23:59:59.999) of the week for a given date.
 * @param {Date | string} dateInput - The date (or string) to find the week boundaries for.
 * @returns {{startOfWeek: Date, endOfWeek: Date} | null} An object containing the start and end Date objects, or null if input is invalid.
 */
export const getWeekBoundaries = (dateInput) => {
    try {
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) {throw new Error("Invalid date input");}
        d.setHours(0, 0, 0, 0);
        const day = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        // Adjust to make Monday day 0, Sunday day 6
        const diffToMonday = d.getDate() - day + (day === 0 ? -6 : 1);
        const startOfWeek = new Date(d.setDate(diffToMonday));
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999); // End of Sunday
        return { startOfWeek, endOfWeek };
    } catch (e) {
        console.error(`Error in getWeekBoundaries with input: ${dateInput}`, e);
        return null;
    }
};

/**
 * Calculates the start (1st, 00:00) and end (last day, 23:59:59.999) of the month for a given date.
 * @param {Date | string} dateInput - The date (or string) to find the month boundaries for.
 * @returns {{startOfMonth: Date, endOfMonth: Date} | null} An object containing the start and end Date objects, or null if input is invalid.
 */
export const getMonthBoundaries = (dateInput) => {
    try {
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) {throw new Error("Invalid date input");}
        const year = d.getFullYear();
        const month = d.getMonth();
        const startOfMonth = new Date(year, month, 1);
        const endOfMonth = new Date(year, month + 1, 0); // Day 0 of next month = last day of current month
        endOfMonth.setHours(23, 59, 59, 999);
        return { startOfMonth, endOfMonth };
    } catch (e) {
        console.error(`Error in getMonthBoundaries with input: ${dateInput}`, e);
        return null;
    }
};

/**
 * Formats a date string (or Date object) into DD/MM/YYYY format (Australian locale).
 * @param {string | Date | null} dateString - The date string or object to format.
 * @returns {string} The formatted date, or an empty string if the input is invalid or null.
 */
export const formatDate = (dateString) => {
    if (!dateString) {return '';}
    try {
        const seconds = dateString?.seconds ?? dateString?._seconds;
        const nanoseconds = dateString?.nanoseconds ?? dateString?._nanoseconds ?? 0;
        const date = dateString?.toDate ? dateString.toDate() :
            (typeof seconds === 'number' ?
                new Date((seconds * 1000) + (nanoseconds / 1e6)) :
                new Date(dateString));
        if (isNaN(date.getTime())) {
            // Don't warn for clearly invalid inputs like empty strings or null passed explicitly
            if (dateString !== '' && dateString !== null) {
                console.warn(`Invalid date value passed to formatDate: ${dateString}`);
            }
            return '';
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (e) {
        console.error(`Error formatting date: ${dateString}`, e);
        return '';
    }
};

/**
 * Formats a date/timestamp into DD/MM/YYYY HH:mm format.
 * @param {string | Date | object | null} dateVal - The date or Firestore timestamp.
 * @returns {string} Formatted string.
 */
export const formatDateTime = (dateVal) => {
    if (!dateVal) {return '';}
    try {
        const seconds = dateVal?.seconds ?? dateVal?._seconds;
        const nanoseconds = dateVal?.nanoseconds ?? dateVal?._nanoseconds ?? 0;
        const date = dateVal?.toDate ? dateVal.toDate() :
            (typeof seconds === 'number' ?
                new Date((seconds * 1000) + (nanoseconds / 1e6)) :
                new Date(dateVal));
        if (isNaN(date.getTime())) {return '';}
        const d = formatDate(date);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${d} ${hours}:${minutes}`;
    } catch {
        return '';
    }
};

/**
 * Formats a date string (or Date object) into short DD/MM format.
 * @param {string | Date | null} dateString - The date string or object to format.
 * @returns {string} The formatted date, or an empty string if the input is invalid or null.
 */
export const formatDateShort = (dateString) => {
    if (!dateString) {return '';}
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            if (dateString !== '' && dateString !== null) {
                console.warn(`Invalid date value passed to formatDateShort: ${dateString}`);
            }
            return '';
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}/${month}`;
    } catch (e) {
        console.error(`Error formatting short date: ${dateString}`, e);
        return '';
    }
};

/**
 * Finds the soonest valid date between two date strings. Handles null/invalid values.
 * @param {string | Date | null} dateA - First date string or object.
 * @param {string | Date | null} dateB - Second date string or object.
 * @returns {string | null} The earlier of the two valid date strings (in original format), or null if neither is valid.
 */
export function soonestDate(dateA, dateB) {
    let d1 = null, d2 = null;
    try {
        if (dateA) {d1 = new Date(dateA);}
        if (isNaN(d1?.getTime())) {d1 = null;} // Invalidate if parsing failed
    } catch { d1 = null; }
    try {
        if (dateB) {d2 = new Date(dateB);}
        if (isNaN(d2?.getTime())) {d2 = null;}
    } catch { d2 = null; }

    if (d1 && d2) {
        return d1 <= d2 ? dateA : dateB; // Return original string/object
    }
    // If only one is valid, return that original value
    return dateA && d1 ? dateA : (dateB && d2 ? dateB : null);
}
